import React, { useEffect, useState } from "react";

import { omit } from "lodash";

import { useLocation, useNavigate, useTranslation } from "utils";

import { Button, Flex, Icon, MainPage, SoilSightMarginChart, Tag, Typography } from "components";

import { GoDeeperCard } from "pages/app/scenario-landing/components/GoDeeperCard";
import { useSoilSightOnboardingSettingPatch } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";

const DATA_BASIS_CARDS = [
  { key: "soilType", iconName: "tabler/map-pin" },
  { key: "climateZone", iconName: "tabler/cloud" },
  { key: "regionalYields", iconName: "klim/crops-matured" },
  { key: "yourInputs", iconName: "tabler/adjustments-horizontal" },
] as const;

export const ScenarioLandingPage = () => {
  const { t } = useTranslation(["scenarioAnalysis", "global", "meta"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { patchLandingSeen } = useSoilSightOnboardingSettingPatch();

  const state = location.state as { entryPoint?: string; fromOnboarding?: boolean } | null;
  // Frozen on mount: we remove entryPoint from the router state below, so a reload doesn't send the old one again.
  const [entryPoint] = useState(() => state?.entryPoint ?? "direct");

  useEffect(() => {
    if (!state?.entryPoint) return;
    // Only entryPoint goes. useSoilSightFlowRedirect and handleStart still read the rest.
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: omit(state, "entryPoint") });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  // Passing the landing is remembered on the draft (later entries skip it); router state is
  // forwarded so an import's dropped-crops info survives the landing detour to step 1.
  const handleStart = () => {
    patchLandingSeen();
    navigate("/scenarios-onboarding/step-1", { state: location.state });
  };

  const navigationCrumbs = {
    crumbs: [{ name: t("global:header.home"), link: "/home" }, { name: t("scenarioAnalysis:scenarioLanding.pageTitle") }],
    additionalData: <></>,
  };

  return (
    <MainPage
      fullWidth
      pageName="scenario-landing"
      navigationCrumbs={navigationCrumbs}
      share={{ title: t("meta:soilSight.title") }}
      entryPoint={entryPoint}
      // We skip returns from the wizard. They are not new arrivals.
      productEvent={state?.fromOnboarding ? undefined : "soilsights_onboarding:landing_view"}
      productEventProperties={{ entry_point: entryPoint }}
    >
      <Flex vertical={true} gap="0.75rem" className="margin-bottom-400">
        <Tag colour="yellow" text={t("scenarioAnalysis:onboarding.betaBadge")} className="scenario-landing-new-badge" />
        <Flex gap="4rem" justify="space-between" align="center" className="scenario-landing-hero">
          <Flex flex={1} vertical={true} gap="3rem" className="scenario-landing-hero__content">
            <Flex vertical={true} gap="1rem">
              <Typography variant="h1">{t("scenarioAnalysis:scenarioLanding.hero.headline")}</Typography>
              <Typography variant="paragraph-l" className="scenario-landing-hero-sub">
                {t("scenarioAnalysis:scenarioLanding.hero.subheadline")}
              </Typography>
            </Flex>
            <Flex gap="1rem" align="center" className="scenario-landing-ctas">
              <Button type="primary" onClick={handleStart} productEvent="soilsights_onboarding:flow_start" dataTestId="scenario-landing-start-cta">
                {t("scenarioAnalysis:scenarioLanding.hero.startCta")}
              </Button>
            </Flex>
          </Flex>
          <div className="scenario-landing-hero__chart">
            <SoilSightMarginChart
              className="scenario-landing-hero__chart-body"
              caption={t("scenarioAnalysis:scenarioLanding.hero.chart.caption")}
              calloutValue={t("scenarioAnalysis:scenarioLanding.hero.chart.calloutValue")}
              calloutDescription={t("scenarioAnalysis:scenarioLanding.hero.chart.calloutDescription")}
              chartLabel={t("scenarioAnalysis:scenarioLanding.hero.chart.chartLabel")}
              legend={{
                baseline: t("scenarioAnalysis:preLogin.landing.hero.legendBaseline"),
                change: t("scenarioAnalysis:preLogin.landing.hero.legendChange"),
              }}
            />
          </div>
        </Flex>
      </Flex>

      <Flex vertical={true} gap="2rem">
        <div className="scenario-landing-data-basis">
          <Typography variant="h4">{t("scenarioAnalysis:scenarioLanding.dataBasis.heading")}</Typography>
          <Flex gap="1.25rem" className="scenario-landing-data-basis__cards">
            {DATA_BASIS_CARDS.map((card) => (
              <Flex key={card.key} flex={1} vertical={true} align="center" gap="1.5rem" className="scenario-landing-data-basis__card">
                <span className="scenario-landing-data-basis__icon">
                  <Icon iconName={card.iconName} size="m-light" />
                </span>
                <Flex vertical={true} align="center" gap="0.25rem" className="scenario-landing-data-basis__text">
                  <Typography variant="h6" className="scenario-landing-data-basis__title">
                    {t(`scenarioAnalysis:scenarioLanding.dataBasis.${card.key}.title`)}
                  </Typography>
                  <Typography variant="paragraph-m" color="grey-600" className="scenario-landing-data-basis__subtitle">
                    {t(`scenarioAnalysis:scenarioLanding.dataBasis.${card.key}.subtitle`)}
                  </Typography>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </div>

        <GoDeeperCard />
      </Flex>
    </MainPage>
  );
};
